import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';

const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  const heroSlide = {
    title: "AI-Driven Custom Software",
    subtitle: "For the Future",
    description: "SR NEXT helps you build intelligent, scalable, and future-ready solutions—powered by AI, built by experts.",
    cta: "Get a Free Consultation"
  };

  const stats = [
    { value: "25+", label: "Successful Projects" },
    { value: "98%", label: "Client Satisfaction" },
    { value: "3+", label: "Years Experience" },
    { value: "12+", label: "Global Reach" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (videoRef.current) {
        videoRef.current.style.transform = `translateY(${window.scrollY * 0.3}px) scale(1.05)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollProgress = Math.min(scrollY / 500, 1);
  const primaryOpacity = Math.max(1 - scrollProgress * 0.7, 0.3);
  const secondaryOpacity = Math.max(1 - scrollProgress * 0.5, 0.4);
  const overlayOpacity = scrollProgress * 0.5;
  const scale = Math.max(1 - scrollProgress * 0.05, 0.95);

  return (
    <section className="hero-section-bg relative overflow-hidden" style={{ minHeight: '80vh' }}>
      <video
        ref={videoRef}
        className="hero-section-video-bg"
        src="/videos/tech1.mp4"
        poster="/images/video-poster.jpg" 
        autoPlay
        muted
        loop
        playsInline
        preload="metadata" 
        style={{
          position: 'absolute',
          inset: 0,
          width: '100vw', 
          height: '100vh', 
          minWidth: '100%',
          minHeight: '100%',
          objectFit: 'cover',
          zIndex: 0,
          transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1), opacity 0.2s cubic-bezier(0.4,0,0.2,1)',
          // filter: 'blur(8px)',
          opacity: 2
        }}
      />

      <div className="hero-section-opacity-overlay" style={{ opacity: overlayOpacity }} />

      <div
        className="hero-section-content"
        style={{
          opacity: primaryOpacity,
          transform: `translateY(${scrollY * 0.3}px) scale(${scale})`,
          transition: 'opacity 0.1s ease-out, transform 0.1s ease-out',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div className="flex flex-col md:flex-row md:items-start w-full gap-8 mb-8">
          <div className="flex-1 flex flex-col justify-start">
            <div className="hero-section-label" style={{ opacity: Math.max(1 - scrollProgress * 0.6, 0.4) }}>
              <div className="hero-section-label-bar"></div>
              <span>SRNEXT</span>
            </div>
            <h1 className="hero-section-title" style={{ opacity: Math.max(1 - scrollProgress * 0.5, 0.5) }}>
              <span>{heroSlide.title}</span>
              <span className="accent">{heroSlide.subtitle}</span>
            </h1>
            <p className="hero-section-desc" style={{ opacity: Math.max(1 - scrollProgress * 0.6, 0.4) }}>
              {heroSlide.description}
            </p>
            <div className="hero-section-cta" style={{ opacity: Math.max(1 - scrollProgress * 0.7, 0.3) }}>
              <button
                className="hero-section-btn-secondary"
                onClick={() => navigate('/contact-us')}
              >
                {heroSlide.cta}
                <ArrowRight style={{ marginLeft: 8 }} size={20} />
              </button>
              <button
                className="hero-section-btn-secondary"
                onClick={() => navigate('/our-work')}
              >
                <Play style={{ marginRight: 8 }} size={20} />
                Explore Our Work
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 hero-section-stats" style={{ opacity: secondaryOpacity }}>
          {stats.map((stat, index) => (
            <div
              key={index}
              className="hero-section-stat"
              style={{
                transform: `translateY(${scrollY * (0.1 + index * 0.05)}px)`,
                opacity: Math.max(1 - scrollProgress * 0.5, 0.5),
                transitionDelay: `${index * 0.1}s`
              }}
            >
              <div className="hero-section-stat-value">{stat.value}</div>
              <div className="hero-section-stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;